import { css } from "@emotion/react";
import { Button } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { match } from "ts-pattern";

import CreateAccountLead from "~/components/common/CreateAccountLead";
import { ResponseAnswer } from "~/components/database/detail/database-content/types";
import { updateResponseAnswer } from "~/components/database/detail/response-modal-content/response-form";
import InputArea from "~/components/database/detail/response-modal-content/response-form/InputArea";
import { getIsQuestTaskVerifyApplied } from "~/components/quest/detail/lib";
import {
  Props,
  useCurrentUserQuestProgress,
} from "~/components/quest/page/[id]/lib";
import { getQuestDurationStatus } from "~/domains/quest/lib";
import { PageName } from "~/lib/client/logger/base";
import { useActivityLogger } from "~/lib/client/logger/useActivityLogger";
import { Sdk } from "~/lib/graphql/client";
import { useAppContext } from "~/pages/_app";

const pageName: PageName = "questDetail";

export const FormResponseArea = ({
  client,
  form,
  quest,
  questTask,
  setCompleteQuestTask,
}: {
  client: Sdk;
  quest: Props["quest"];
  questTask: Props["quest"]["questTasks"][number];
  form: NonNullable<Props["quest"]["questTasks"][number]["form"]>;
  setCompleteQuestTask: (questTaskId: number) => void;
}) => {
  const [responseAnswers, setResponseAnswers] = useState<ResponseAnswer[]>(
    (form?.questions ?? []).map((question) => ({
      questionId: question.id,
      answerTypeEnum: question.answerType,
    }))
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidPerQuestion, setIsValidPerQuestion] = useState<
    Record<number, boolean>
  >(
    (form?.questions ?? []).reduce(
      (acc, question) => ({ ...acc, [question.id]: false }),
      {}
    )
  );
  // 入力された値がすべて valid かどうか
  const isValid = Object.values(isValidPerQuestion).every((isValid) => isValid);

  const { currentUser } = useAppContext();
  const result = useCurrentUserQuestProgress(
    client,
    [quest.id],
    [questTask.id]
  );

  const sendActivityLog = useActivityLogger();

  const submitResponse = useCallback(async () => {
    setIsSubmitting(true);
    await client.createResponse({
      input: {
        questTaskId: questTask.id,
        answers: responseAnswers,
        formId: form.id,
      },
    });
    await result.refetch();
    setIsSubmitting(false);
    setCompleteQuestTask(questTask.id);
  }, [
    client,
    form.id,
    questTask.id,
    responseAnswers,
    result,
    setCompleteQuestTask,
  ]);

  const handleOnFocusInput = (questionId: number) => async () => {
    if (!sendActivityLog) return;

    await sendActivityLog({
      pageName,
      actionName: `focus-${questionId}`,
    });
  };

  const isQuestTaskVerifyApplied = getIsQuestTaskVerifyApplied(
    result,
    questTask
  );
  const questDurationStatus = getQuestDurationStatus(quest);

  const disabled =
    isQuestTaskVerifyApplied ||
    isSubmitting ||
    !currentUser ||
    questDurationStatus !== "OPENING" ||
    !isValid;

  useEffect(() => {
    if (isQuestTaskVerifyApplied) {
      setCompleteQuestTask(questTask.id);
    }
  }, [isQuestTaskVerifyApplied, setCompleteQuestTask, questTask.id]);

  useEffect(() => {
    (async () => {
      await result.refetch();
    })();
  }, [result]);

  if (!result.isFetched) return null;

  return (
    <div>
      {form.questions.map((question) => {
        const responseAnswerInput = responseAnswers.find(
          (responseAnswer) => responseAnswer.questionId === question.id
        );

        if (!responseAnswerInput) return null;

        return (
          <div key={question.id}>
            <h4>{question.title}</h4>
            <p>{question.description}</p>
            {currentUser ? (
              <div onFocus={handleOnFocusInput(question.id)}>
                <InputArea
                  question={question}
                  updateResponseAnswer={updateResponseAnswer(
                    question.id,
                    setResponseAnswers
                  )}
                  setIsValid={(isValid) =>
                    setIsValidPerQuestion((prev) => ({
                      ...prev,
                      [question.id]: isValid,
                    }))
                  }
                  responseAnswerInput={responseAnswerInput}
                />
              </div>
            ) : (
              <CreateAccountLead label={"投稿するにはログインが必要です。"} />
            )}
          </div>
        );
      })}
      <div css={buttonContainerStyle}>
        <Button
          variant="contained"
          color="primary"
          onClick={submitResponse}
          disabled={disabled}
        >
          {match({
            isQuestTaskVerifyApplied,
            isSubmitting,
            disabled,
            login: !!currentUser,
          })
            .with({ isQuestTaskVerifyApplied: true }, () => "回答済み")
            .with({ login: false }, () => "回答する")
            .with({ isSubmitting: true }, () => "送信中")
            .with({ disabled: true }, () => "回答できません")
            .with(
              { isQuestTaskVerifyApplied: false, disabled: false },
              () => "回答する"
            )
            .exhaustive()}
        </Button>
      </div>
    </div>
  );
};

const buttonContainerStyle = css`
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
`;
